import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Task, ViewMode } from "@thepocman/gantt-task-react";
import {
  getColumnWidth as getTimelineColumnWidth,
  getTimelineScrollLeft,
} from "./timelineUtils";

type UseTimelineScrollArgs = {
  rightPaneRef: React.RefObject<HTMLDivElement | null>;
  tasks: Task[];
  ganttTasks: Task[];
  viewMode: ViewMode;
  centerOnDate?: Date;
  centerOnDateRequestId?: number;
  onHorizontalScroll?: () => void;
};

export function useTimelineScroll({
  rightPaneRef,
  tasks,
  ganttTasks,
  viewMode,
  centerOnDate,
  centerOnDateRequestId = 0,
  onHorizontalScroll,
}: UseTimelineScrollArgs) {
  const programmaticScrollTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const manualScrollTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const markProgrammaticScroll = () => {
    isProgrammaticScrollRef.current = true;
    if (programmaticScrollTimerRef.current) {
      clearTimeout(programmaticScrollTimerRef.current);
    }
    programmaticScrollTimerRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (programmaticScrollTimerRef.current) {
        clearTimeout(programmaticScrollTimerRef.current);
      }
      manualScrollTimersRef.current.forEach((timer) => clearTimeout(timer));
      manualScrollTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!centerOnDate || !tasks.length) return;

    const minTaskDate = new Date(
      Math.min(...tasks.map((task) => task.start.getTime())),
    );
    let ganttStartDate: Date;
    if (viewMode === ViewMode.Day) {
      ganttStartDate = dayjs(minTaskDate)
        .startOf("day")
        .subtract(1, "day")
        .toDate();
    } else if (viewMode === ViewMode.Month) {
      ganttStartDate = dayjs(minTaskDate)
        .subtract(1, "month")
        .startOf("month")
        .toDate();
    } else {
      ganttStartDate = dayjs(minTaskDate)
        .subtract(1, "year")
        .startOf("year")
        .toDate();
    }

    const columnWidth = getTimelineColumnWidth(viewMode);
    const paneWidth = rightPaneRef.current?.clientWidth ?? 0;
    const halfUnits = Math.round(paneWidth / 2 / columnWidth);

    let centeredDate: Date;
    if (viewMode === ViewMode.Day) {
      centeredDate = dayjs(centerOnDate).subtract(halfUnits, "day").toDate();
    } else if (viewMode === ViewMode.Month) {
      centeredDate = dayjs(centerOnDate).subtract(halfUnits, "month").toDate();
    } else {
      centeredDate = dayjs(centerOnDate).subtract(halfUnits, "year").toDate();
    }

    if (centeredDate.getTime() < ganttStartDate.getTime()) {
      centeredDate = ganttStartDate;
    }

    const targetScrollLeft = getTimelineScrollLeft(
      centeredDate,
      ganttStartDate,
      viewMode,
      columnWidth,
    );

    markProgrammaticScroll();
    manualScrollTimersRef.current.forEach((timer) => clearTimeout(timer));
    manualScrollTimersRef.current = [];

    const applyManualHorizontalScroll = () => {
      const ganttRoot = rightPaneRef.current?.querySelector<HTMLElement>(
        '[class*="_ganttTaskRoot_"]',
      );
      if (!ganttRoot) return;

      ganttRoot.scrollLeft = targetScrollLeft;
    };

    applyManualHorizontalScroll();
    manualScrollTimersRef.current.push(
      setTimeout(applyManualHorizontalScroll, 0),
    );
    manualScrollTimersRef.current.push(
      setTimeout(applyManualHorizontalScroll, 80),
    );
    manualScrollTimersRef.current.push(
      setTimeout(applyManualHorizontalScroll, 220),
    );
  }, [centerOnDate, centerOnDateRequestId, rightPaneRef, tasks, viewMode]);

  useEffect(() => {
    const ganttRoot = rightPaneRef.current?.querySelector<HTMLElement>(
      '[class*="_ganttTaskRoot_"]',
    );
    if (!ganttRoot) return;

    const onScroll = () => {
      if (!isProgrammaticScrollRef.current) {
        onHorizontalScroll?.();
      }
    };

    ganttRoot.addEventListener("scroll", onScroll, { passive: true });
    return () => ganttRoot.removeEventListener("scroll", onScroll);
  }, [ganttTasks, onHorizontalScroll, rightPaneRef, viewMode]);
}
